import type { SupabaseClient } from '@supabase/supabase-js';

/**
 * News and market reports, written by the FarmTradeDirect team.
 *
 * The page previously held eight invented articles attributed to
 * "FarmTradeDirect" — including fabricated DEFRA payment rates and fabricated
 * prices from named auction marts. Nothing here ships with sample content, for
 * the reason set out in the news_posts migration.
 */

export interface NewsPost {
  id: string;
  title: string;
  slug: string | null;
  category: string | null;
  excerpt: string | null;
  body: string | null;
  author: string | null;
  image_url: string | null;
  source_name: string | null;
  source_url: string | null;
  published_at: string | null;
  is_published: boolean;
  created_at: string;
  updated_at: string;
}

export const NEWS_CATEGORIES = [
  'Market Report',
  'Auction Results',
  'Industry News',
  'Policy & Regulation',
  'Machinery',
  'FarmTradeDirect News',
];

const COLUMNS =
  'id, title, slug, category, excerpt, body, author, image_url, source_name, ' +
  'source_url, published_at, is_published, created_at, updated_at';

export async function fetchNews(supabase: SupabaseClient): Promise<NewsPost[]> {
  const { data, error } = await supabase
    .from('news_posts')
    .select(COLUMNS)
    .eq('is_published', true)
    .order('published_at', { ascending: false, nullsFirst: false })
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data ?? []) as NewsPost[];
}

/** Every post, drafts included. RLS restricts this to admins. */
export async function fetchAllNews(supabase: SupabaseClient): Promise<NewsPost[]> {
  const { data, error } = await supabase
    .from('news_posts')
    .select(COLUMNS)
    .order('published_at', { ascending: false, nullsFirst: false })
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data ?? []) as NewsPost[];
}

export async function createNewsPost(
  supabase: SupabaseClient,
  post: Partial<NewsPost> & { title: string }
): Promise<void> {
  const { error } = await supabase.from('news_posts').insert(post);
  if (error) throw error;
}

export async function saveNewsPost(
  supabase: SupabaseClient,
  id: string,
  patch: Partial<NewsPost>
): Promise<void> {
  const { error } = await supabase.from('news_posts').update(patch).eq('id', id);
  if (error) throw error;
}

export async function deleteNewsPost(supabase: SupabaseClient, id: string): Promise<void> {
  const { error } = await supabase.from('news_posts').delete().eq('id', id);
  if (error) throw error;
}

export function slugify(title: string): string {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);
}

/** Roughly how long it takes to read, at 200 words a minute. */
export function readTime(post: NewsPost): string {
  const words = `${post.excerpt ?? ''} ${post.body ?? ''}`.trim().split(/\s+/).length;
  return `${Math.max(1, Math.round(words / 200))} min read`;
}

export function newsDate(post: NewsPost): string {
  const iso = post.published_at ?? post.created_at;
  return new Date(iso).toLocaleDateString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric',
  });
}

const CATEGORY_COLOURS: Record<string, string> = {
  'Market Report': 'bg-green-100 text-green-800',
  'Auction Results': 'bg-blue-100 text-blue-800',
  'Industry News': 'bg-amber-100 text-amber-800',
  'Policy & Regulation': 'bg-purple-100 text-purple-800',
  Machinery: 'bg-slate-100 text-slate-700',
  'FarmTradeDirect News': 'bg-secondary text-secondary-foreground',
};

export function categoryColour(category: string | null): string {
  return CATEGORY_COLOURS[category ?? ''] ?? 'bg-secondary text-secondary-foreground';
}
